import { FunctionComponent, useMemo, useState } from 'react';
import LineChart from '../LineChart';
import { SimpleObject } from '../../types';
import {
  EcgBandasTension,
  EcgNivelTension,
  ECG_BANDAS_COLORS
} from './estudioConexionResultadoTecnicoShared';

// ═══════════════════════════════════════════════════════════════════════════
// Gráfico de tensión diaria con filtro MT/BT — NORM-018 A §3.1.2 G.1 / §3.2.2
//
// Mismo componente/contrato visual usado por Fase 1 (resultados de conexión)
// y Fase 2 (resultados de mitigaciones). El usuario elige **nivel** (Media
// tensión | Baja tensión). En cada modo:
//   - Se muestran solo las series de buses del nivel elegido (deciles).
//   - Se dibuja SIEMPRE el corredor absoluto (0.85 / 1.10 p.u.).
//   - Se dibujan SOLO las bandas relativas del nivel elegido.
//
// Fuente de datos por nivel: `tensiones_bus_pu_deciles_por_nivel.{todos,mt,bt}`
// (schema 1.1.0). Fallback legacy: `tensiones_bus_pu_deciles` (vista "todos").
//
// Bandas: leídas del bloque `bandas_tension` en la raíz del artefacto —
// el frontend **no** recalcula bandas normativas.
// ═══════════════════════════════════════════════════════════════════════════

const DECILE_ORDER = ['p0', 'p2', 'p5', 'p10', 'p20', 'p30', 'p40', 'p50', 'p60', 'p70', 'p80', 'p90', 'p95', 'p98', 'p100'];
// Extremos inferior (p0/p2/p5) y superior (p95/p98/p100) en grises simétricos;
// deciles centrales (p10–p90) con la paleta de colores existente.
const DECILE_COLORS = [
  '#d0d0d0', // p0
  '#b0b0b0', // p2
  '#909090', // p5
  '#1b9e77', // p10
  '#d95f02', // p20
  '#7570b3', // p30
  '#e7298a', // p40
  '#66a61e', // p50
  '#e6ab02', // p60
  '#a6761d', // p70
  '#666666', // p80
  '#01665e', // p90
  '#909090', // p95
  '#b0b0b0', // p98
  '#d0d0d0'  // p100
];

type EcgDecileBundle = Record<string, number[]>;

function pickField<T>(o: SimpleObject | null | undefined, snake: string, camel: string): T | undefined {
  if (!o || typeof o !== 'object') return undefined;
  const a = o[snake];
  if (a !== undefined && a !== null) return a as T;
  const b = o[camel];
  if (b !== undefined && b !== null) return b as T;
  return undefined;
}

function parseDecilBundle(raw: unknown): EcgDecileBundle | null {
  if (!raw || typeof raw !== 'object') return null;
  const o = raw as SimpleObject;
  const out: EcgDecileBundle = {};
  let any = false;
  for (const k of DECILE_ORDER) {
    const arr = o[k];
    if (Array.isArray(arr)) {
      out[k] = arr.map((x) => Number(x));
      any = true;
    }
  }
  return any ? out : null;
}

/** Lee deciles por nivel (schema 1.1) con fallback a los deciles planos (schema 1.0). */
function decilesParaNivel(dj: SimpleObject, nivel: EcgNivelTension): EcgDecileBundle | null {
  const porNivel = pickField<SimpleObject>(
    dj,
    'tensiones_bus_pu_deciles_por_nivel',
    'tensionesBusPuDecilesPorNivel'
  );
  if (porNivel && typeof porNivel === 'object') {
    const rama = (porNivel as SimpleObject)[nivel];
    const parsed = parseDecilBundle(rama);
    if (parsed) return parsed;
    if (nivel !== 'todos') return null;
  }
  if (nivel !== 'todos') return null;
  const legacy = pickField<SimpleObject>(dj, 'tensiones_bus_pu_deciles', 'tensionesBusPuDeciles');
  return parseDecilBundle(legacy);
}

function nivelDisponible(dj: SimpleObject, nivel: EcgNivelTension, bandas: EcgBandasTension): boolean {
  if (nivel === 'todos') return decilesParaNivel(dj, 'todos') !== null;
  if (nivel === 'mt' && !bandas.mtPu) return false;
  if (nivel === 'bt' && !bandas.btPu) return false;
  return decilesParaNivel(dj, nivel) !== null;
}

function conteoBuses(dj: SimpleObject, nivel: EcgNivelTension): number | null {
  const key =
    nivel === 'mt' ? ['n_buses_mt', 'nBusesMt'] : nivel === 'bt' ? ['n_buses_bt', 'nBusesBt'] : ['n_buses', 'nBuses'];
  const v = pickField<number>(dj, key[0], key[1]);
  return typeof v === 'number' && Number.isFinite(v) ? v : null;
}

function etiquetaNivel(n: EcgNivelTension): string {
  if (n === 'mt') return 'Media tensión';
  if (n === 'bt') return 'Baja tensión';
  return 'Todos los buses';
}

function etiquetaDensidad(d: string | null): string | null {
  if (!d) return null;
  const map: Record<string, string> = {
    alta: 'alta densidad',
    media: 'media densidad',
    baja: 'baja densidad',
    muy_baja: 'muy baja densidad'
  };
  return map[d] ?? d;
}

/** Eje temporal: minutos desde 00:00 del día (paso `intervalo_minutos`). */
function minutesToHHmm(m: number): string {
  let mm = Math.round(m) % (24 * 60);
  if (mm < 0) mm += 24 * 60;
  const h = Math.floor(mm / 60);
  const min = mm % 60;
  return `${String(h).padStart(2, '0')}:${String(min).padStart(2, '0')}`;
}

function tickIntervalEvery30(minutes: number[]) {
  const lastIdx = minutes.length - 1;
  return (value: unknown, index: number) => {
    const m = Number(value);
    if (!Number.isFinite(m)) return false;
    if (index === lastIdx) return true;
    return m % 30 === 0;
  };
}

const EstudioConexionTensionDiariaChart: FunctionComponent<{
  datosJson: SimpleObject;
  bandas: EcgBandasTension;
  /** Si se omite, el componente fija por defecto MT (si hay) o `todos`. */
  nivelInicial?: EcgNivelTension;
  /** Alto del gráfico. */
  height?: number;
  /** Cabecera extra (ej.: nombre del caso en comparativos Fase 2). */
  titulo?: string;
}> = ({ datosJson, bandas, nivelInicial, height = 380, titulo }) => {
  const hayMt = !!bandas.mtPu && decilesParaNivel(datosJson, 'mt') !== null;
  const hayBt = !!bandas.btPu && decilesParaNivel(datosJson, 'bt') !== null;
  const hayTodos = decilesParaNivel(datosJson, 'todos') !== null;

  const defaultNivel: EcgNivelTension = hayMt ? 'mt' : hayBt ? 'bt' : 'todos';
  const [nivel, setNivel] = useState<EcgNivelTension>(nivelInicial ?? defaultNivel);

  const nivelEfectivo: EcgNivelTension = nivelDisponible(datosJson, nivel, bandas) ? nivel : defaultNivel;

  const xData = useMemo(() => {
    const raw = pickField<unknown[]>(datosJson, 'instantes_muestra', 'instantesMuestra');
    if (!Array.isArray(raw) || raw.length === 0) return null;
    return raw.map((x) => Number(x));
  }, [datosJson]);

  const deciles = useMemo(() => decilesParaNivel(datosJson, nivelEfectivo), [datosJson, nivelEfectivo]);

  const chartPayload = useMemo(() => {
    if (!xData || !deciles) return null;

    const bandaRelativa =
      nivelEfectivo === 'mt' ? bandas.mtPu : nivelEfectivo === 'bt' ? bandas.btPu : null;

    const makeConst = (v: number) => xData.map(() => v);

    type ChartSerie = {
      label: string;
      data: number[];
      curve: 'linear';
      color: string;
      dashed?: boolean;
    };

    const bandaSeries: ChartSerie[] = [
      {
        label: `Límite absoluto (${bandas.absolutoPu.max.toFixed(2)} p.u.)`,
        data: makeConst(bandas.absolutoPu.max),
        curve: 'linear',
        color: ECG_BANDAS_COLORS.absoluto
      },
      {
        label: `Límite absoluto (${bandas.absolutoPu.min.toFixed(2)} p.u.)`,
        data: makeConst(bandas.absolutoPu.min),
        curve: 'linear',
        color: ECG_BANDAS_COLORS.absoluto
      }
    ];

    if (bandaRelativa) {
      const colorRel = nivelEfectivo === 'mt' ? ECG_BANDAS_COLORS.mt : ECG_BANDAS_COLORS.bt;
      const prefijo = nivelEfectivo === 'mt' ? 'Banda MT' : 'Banda BT';
      bandaSeries.push(
        {
          label: `${prefijo} máx (${bandaRelativa.max.toFixed(3)} p.u.)`,
          data: makeConst(bandaRelativa.max),
          curve: 'linear',
          color: colorRel,
          dashed: true
        },
        {
          label: `${prefijo} mín (${bandaRelativa.min.toFixed(3)} p.u.)`,
          data: makeConst(bandaRelativa.min),
          curve: 'linear',
          color: colorRel,
          dashed: true
        }
      );
    }

    const decileSeries: ChartSerie[] = [];
    for (let i = 0; i < DECILE_ORDER.length; i++) {
      const key = DECILE_ORDER[i];
      const arr = deciles[key];
      if (Array.isArray(arr)) {
        decileSeries.push({
          label: key.toUpperCase(),
          data: arr,
          curve: 'linear',
          color: DECILE_COLORS[i % DECILE_COLORS.length]
        });
      }
    }

    const all = [...bandaSeries, ...decileSeries];
    return {
      series: all.map((s) => ({ label: s.label, data: s.data, curve: s.curve })),
      colors: all.map((s) => s.color)
    };
  }, [xData, deciles, bandas, nivelEfectivo]);

  const intervalo = pickField<number>(datosJson, 'intervalo_minutos', 'intervaloMinutos');
  const yLabel = 'Tensión en buses (p.u.)';

  const densidadStr = etiquetaDensidad(bandas.densidad);
  const hintCriterio = densidadStr
    ? `Criterios mostrados según densidad del alimentador en este estudio (${densidadStr}).`
    : 'Criterios mostrados según densidad del alimentador en este estudio.';

  const nBuses = conteoBuses(datosJson, nivelEfectivo);

  const avisos: string[] = [];
  if (bandas.schemaLegacy) {
    avisos.push(
      'Estudio pre-NORM-018 A: se muestra la vista combinada (todos los buses) con el corredor absoluto.'
    );
  }
  if (nivel !== nivelEfectivo) {
    avisos.push(`El nivel seleccionado no tiene buses en este estudio; se muestra "${etiquetaNivel(nivelEfectivo)}".`);
  }

  const filtroHabilitado = !bandas.schemaLegacy && (hayMt || hayBt || hayTodos);

  return (
    <div>
      {titulo && (
        <h4 className='vdr-result-panel-title'>{titulo}</h4>
      )}
      <div className='ecg-form-row' style={{ gap: '0.75rem', flexWrap: 'wrap', marginBottom: '0.5rem' }}>
        {filtroHabilitado && (
          <div className='ecg-form-field' style={{ maxWidth: 260 }}>
            <label className='ecg-label'>Nivel de tensión</label>
            <select
              className='ecg-input'
              value={nivel}
              onChange={(e) => setNivel(e.target.value as EcgNivelTension)}>
              <option value='mt' disabled={!hayMt}>
                Media tensión{!hayMt ? ' (sin buses)' : ''}
              </option>
              <option value='bt' disabled={!hayBt}>
                Baja tensión{!hayBt ? ' (sin buses)' : ''}
              </option>
              <option value='todos' disabled={!hayTodos}>
                Todos los buses
              </option>
            </select>
          </div>
        )}
        {nBuses !== null && (
          <div className='ecg-form-field' style={{ maxWidth: 180 }}>
            <label className='ecg-label'>Buses del nivel</label>
            <span className='ecg-input vdr-result-static-value'>
              {nBuses}
            </span>
          </div>
        )}
      </div>
      <p className='ecg-hint' style={{ margin: '0 0 0.5rem' }}>
        {hintCriterio} El corredor absoluto aplica a todos los buses; las bandas coloreadas corresponden al
        nivel seleccionado.
      </p>
      {avisos.map((a, i) => (
        <p key={i} className='ecg-hint ecg-hint-warn' style={{ margin: '0 0 0.5rem' }}>
          {a}
        </p>
      ))}
      {!xData && (
        <p className='ecg-hint'>
          Sin eje temporal (instantes_muestra) para este bloque.
        </p>
      )}
      {xData && !chartPayload && (
        <p className='ecg-hint'>
          No hay series de deciles para este nivel en este escenario.
        </p>
      )}
      {xData && chartPayload && (
        <LineChart
          height={height}
          xAxis={[
            {
              scaleType: 'point',
              data: xData,
              label:
                intervalo != null && Number.isFinite(intervalo)
                  ? `Hora del día (muestreo cada ${intervalo} min; marcas cada 30 min)`
                  : 'Hora del día (marcas cada 30 min)',
              valueFormatter: (value: number) => minutesToHHmm(Number(value)),
              tickInterval: tickIntervalEvery30(xData)
            }
          ]}
          yAxis={[{ label: yLabel }]}
          series={chartPayload.series}
          colors={chartPayload.colors}
        />
      )}
    </div>
  );
};

export default EstudioConexionTensionDiariaChart;
